import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, RefreshCw, Sparkles, Trash2, Upload, Wand2 } from "lucide-react";
import { addToPictureLibrary, aiImagePrompt, aiImageServices, aiMakeImage, listPictureLibrary, mediaUrl, removeFromPictureLibrary } from "../lib/api";
import { PosterPreview } from "./PostParts";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };
const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const STYLE_LABELS = { photo: "Photo", illustration: "Illustration", render: "3D", minimal: "Minimal", watercolor: "Watercolor" };
const ASPECT_LABELS = { portrait: "Portrait 4:5", square: "Square", landscape: "Wide 16:9" };
const MODES = [["none", "Text only"], ["poster", "Branded poster"], ["mine", "My image"], ["library", "My library"], ["ai", "Create with AI"]];

// What goes under a post's preview for the chosen picture.
export function imageNode(image, headline, sub, tag) {
  if (image.mode === "library" && image.lib) return <img className="picked-img" src={mediaUrl(image.lib.original)} alt="" />;
  if (image.mode === "mine" && image.mine) return <img className="picked-img" src={`data:${image.mine.type};base64,${image.mine.b64}`} alt="" />;
  if (image.mode === "ai" && image.ai) return <img className="picked-img" src={`data:image/png;base64,${image.ai.b64}`} alt="" />;
  if (image.mode === "poster" && headline?.trim()) return <PosterPreview headline={headline} sub={sub} tag={tag} />;
  return null;
}

function MineTab({ image, set }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) { toast.error("Use a PNG, JPEG or WebP image"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Images must be under 15 MB"); return; }
    set({ mine: { filename: file.name, b64: await readAsBase64(file), type: file.type } });
  };
  return <div className="img-mine">
    {image.mine
      ? <div className="img-picked"><img src={`data:${image.mine.type};base64,${image.mine.b64}`} alt="" />
          <div><b>{image.mine.filename}</b>
            <label className="check-inline"><input type="checkbox" checked={image.addLogo} onChange={e => set({ addLogo: e.target.checked })} /> Add my company logo</label>
            <div className="img-actions"><button className="pill" onClick={() => ref.current?.click()}><Upload size={11} /> Replace</button>
              <button className="pill" onClick={() => set({ mine: null })}><Trash2 size={11} /> Remove</button></div></div></div>
      : <div className={`dropzone-modern small ${drag ? "drag" : ""}`} onClick={() => ref.current?.click()} data-testid="img-mine-drop"
          onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={e => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}>
          <ImagePlus size={18} /><b>Choose your own picture</b><small>PNG, JPEG or WebP · drop it here or click</small></div>}
    <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={e => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="img-mine-input" />
  </div>;
}

const free = (it, chosen) => !it.posted && (!it.in_post || it.id === chosen);

// Many pictures kept ready: pick one by hand or let the app pick one at random. A picture that has been posted is greyed out.
function LibraryTab({ image, set }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = () => listPictureLibrary().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const chosen = image.lib?.id;
  const add = async (files) => {
    setBusy(true);
    let n = 0;
    for (const f of Array.from(files)) {
      if (!/^image\/(png|jpe?g|webp)$/.test(f.type)) { toast.error(`${f.name}: use PNG, JPEG or WebP`); continue; }
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: images must be under 15 MB`); continue; }
      try { await addToPictureLibrary(f.name, await readAsBase64(f), image.addLogo); n++; } catch (e) { toast.error(errText(e, `Could not add ${f.name}`)); }
    }
    setBusy(false); if (n) toast.success(`${n} picture${n === 1 ? "" : "s"} added`); load();
  };
  const random = () => {
    const pool = (items || []).filter(it => free(it, null));
    if (!pool.length) { toast.error("No unused pictures left — add more"); return; }
    const others = pool.filter(it => it.id !== chosen);
    const it = (others.length ? others : pool)[Math.floor(Math.random() * (others.length || pool.length))];
    set({ lib: it });
  };
  const remove = async (it) => { try { await removeFromPictureLibrary(it.id); if (chosen === it.id) set({ lib: null }); load(); } catch (e) { toast.error(errText(e, "Could not remove it")); } };
  const unused = (items || []).filter(it => free(it, null)).length;
  return <div className="img-library" data-testid="img-library">
    <div className="lib-bar">
      <button type="button" className="pill" onClick={() => ref.current?.click()} disabled={busy} data-testid="lib-add">
        {busy ? <Loader2 size={11} className="spin" /> : <ImagePlus size={11} />} Add pictures</button>
      <button type="button" className="pill" onClick={random} disabled={!unused} data-testid="lib-random"><RefreshCw size={11} /> Pick one at random</button>
      <label className="check-inline"><input type="checkbox" checked={image.addLogo} onChange={e => set({ addLogo: e.target.checked })} /> Add my logo to new pictures</label>
      {items && <small className="muted">{unused} unused of {items.length}</small>}
    </div>
    <input ref={ref} type="file" multiple accept="image/png,image/jpeg,image/webp" hidden onChange={e => { add(e.target.files); e.target.value = ""; }} data-testid="lib-input" />
    {items && !items.length && <div className="dropzone-modern small" onClick={() => ref.current?.click()}><ImagePlus size={18} /><b>Add many pictures</b><small>Each one can be used for one post · click to choose several</small></div>}
    <div className="lib-grid">
      {(items || []).map(it => {
        const ok = free(it, chosen), on = chosen === it.id;
        return <div key={it.id} className={`lib-tile ${on ? "on" : ""} ${ok ? "" : "used"}`} data-testid={`lib-tile${ok ? "" : "-used"}`}>
          <button type="button" disabled={!ok} onClick={() => set({ lib: on ? null : it })} title={ok ? it.filename : it.posted ? "Posted already" : "In another post"}>
            <img src={mediaUrl(it.original)} alt={it.filename} loading="lazy" />
            {!ok && <span className="lib-badge">{it.posted ? "Posted already" : "In another post"}</span>}
          </button>
          <button type="button" className="lib-x" onClick={() => remove(it)} aria-label="Remove from library"><Trash2 size={11} /></button>
        </div>;
      })}
    </div>
    {!chosen && items?.length > 0 && <small className="muted">Choose a picture, or press “Pick one at random”.</small>}
  </div>;
}

function AiTab({ image, set, text, headline, sub, textModel, onUsed }) {
  const [services, setServices] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { aiImageServices().then(setServices).catch(() => {}); }, []);
  const ready = (services?.services || []).filter(s => s.configured);
  const first = services?.services.find(s => s.id === services.order[0]);

  const suggest = async () => {
    setBusy("prompt");
    try { set({ prompt: (await aiImagePrompt({ text, model: textModel?.provider === "auto" ? undefined : textModel })).prompt }); }
    catch (e) { toast.error(errText(e, "The AI could not suggest a picture")); }
    finally { setBusy(""); }
  };
  const create = async () => {
    setBusy("image");
    try {
      const r = await aiMakeImage({ prompt: image.prompt, style: image.style, aspect: image.aspect, service: image.service === "auto" ? undefined : image.service,
        headline: image.overlay ? headline : "", sub: image.overlay ? sub : "", logo: image.addLogo });
      set({ ai: { b64: r.png_base64, service: r.service } }); onUsed?.();
    } catch (e) { toast.error(errText(e, "Could not create the picture")); }
    finally { setBusy(""); }
  };
  return <div className="img-ai" data-testid="img-ai">
    <div className="img-ai-form">
      <label><span>Describe the picture <small>(or let the AI suggest one from your post)</small></span>
        <textarea rows={3} value={image.prompt} onChange={e => set({ prompt: e.target.value })} data-testid="img-prompt"
          placeholder="e.g. A calm harbour at dawn with soft pink light" /></label>
      <button type="button" className="pill" onClick={suggest} disabled={!text.trim() || busy === "prompt"} data-testid="img-suggest">
        {busy === "prompt" ? <Loader2 size={11} className="spin" /> : <Wand2 size={11} />} Suggest from my post</button>
      <div className="chip-row">{Object.entries(STYLE_LABELS).map(([k, label]) => <button key={k} type="button" className={`tone-chip ${image.style === k ? "on" : ""}`} onClick={() => set({ style: k })}>{label}</button>)}</div>
      <div className="segmented">{Object.entries(ASPECT_LABELS).map(([k, label]) => <button key={k} type="button" className={image.aspect === k ? "on" : ""} onClick={() => set({ aspect: k })}>{label}</button>)}</div>
      <label className="check-inline"><input type="checkbox" checked={image.overlay} onChange={e => set({ overlay: e.target.checked })} data-testid="img-overlay" /> Print my poster headline on the picture</label>
      <label className="check-inline"><input type="checkbox" checked={image.addLogo} onChange={e => set({ addLogo: e.target.checked })} /> Add my company logo</label>
      {services && ready.length > 1 && <label><span>Picture service</span>
        <select value={image.service} onChange={e => set({ service: e.target.value })} data-testid="img-service">
          <option value="auto">Automatic{first ? ` (${first.label})` : ""}</option>
          {ready.map(s => <option key={s.id} value={s.id}>{s.label} · paid</option>)}</select></label>}
      <small className="muted">Each picture is billed to your {first?.label || "image service"} account.</small>
      <button type="button" className="primary-button" onClick={create} disabled={busy === "image" || image.prompt.trim().length < 5} data-testid="img-create">
        {busy === "image" ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} {image.ai ? "Create another" : "Create picture"}</button>
      {busy === "image" && <small className="muted">Creating… this can take up to a minute.</small>}
    </div>
    <div className={`img-ai-result ${image.aspect}`}>
      {image.ai ? <>
        <img src={`data:image/png;base64,${image.ai.b64}`} alt="AI picture" data-testid="img-result" />
        <span className="model-badge"><Sparkles size={10} /> {image.ai.service.label} · paid{image.ai.service.fell_back ? " · took over" : ""}</span>
        <button type="button" className="pill" onClick={create} disabled={busy === "image"}><RefreshCw size={11} /> Try again</button>
      </> : <div className="img-ai-empty">{busy === "image" ? <Loader2 size={20} className="spin" /> : <Sparkles size={20} />}<small>{busy === "image" ? "Creating your picture…" : "Your picture appears here"}</small></div>}
    </div>
  </div>;
}

// Choose the picture for a post: the branded poster, the person's own image, one made by AI, or none.
export default function ImagePanel({ image, onChange, text, headline, sub, tag, onHeadline, onSub, textModel, disabled }) {
  const set = (patch) => onChange({ ...image, ...patch });
  const [aiReady, setAiReady] = useState(false);
  useEffect(() => { aiImageServices().then(r => setAiReady(r.order.length > 0)).catch(() => {}); }, []);
  const modes = MODES.filter(([k]) => k !== "ai" || aiReady);
  return <div className="image-panel" data-testid="image-panel">
    <div className="image-panel-head"><b>Picture</b>
      <div className="segmented" data-testid="img-modes">{modes.map(([k, label]) => <button key={k} type="button" disabled={disabled} className={image.mode === k ? "on" : ""} onClick={() => set({ mode: k })} data-testid={`img-mode-${k}`}>{label}</button>)}</div></div>
    {image.mode === "poster" && <div className="poster-row">
      <PosterPreview headline={headline} sub={sub} tag={tag} />
      <div className="poster-fields">
        <label><span>Poster headline</span><input value={headline} onChange={e => onHeadline(e.target.value)} disabled={disabled} /></label>
        <label><span>Poster line</span><input value={sub} onChange={e => onSub(e.target.value)} disabled={disabled} /></label>
        <small className="muted">Made from your logo and brand colours. The words are printed exactly as typed.</small></div></div>}
    {image.mode === "mine" && <MineTab image={image} set={set} />}
    {image.mode === "library" && <LibraryTab image={image} set={set} />}
    {image.mode === "ai" && aiReady && <>
      <AiTab image={image} set={set} text={text} headline={headline} sub={sub} textModel={textModel} />
      {image.overlay && <div className="two-col"><label><span>Headline on the picture</span><input value={headline} onChange={e => onHeadline(e.target.value)} /></label>
        <label><span>Line under it</span><input value={sub} onChange={e => onSub(e.target.value)} /></label></div>}
      <small className="muted">AI pictures are for backgrounds and art. Use your own photos for your products and people. Words are never drawn by the AI: they are printed by Open CRM, so they are spelled right.</small></>}
    {image.mode === "none" && <small className="muted">This post will go out with text only. For a picture, choose a branded poster, your own image or one from your library.</small>}
  </div>;
}
